import { formatDate } from '../dppApi';

// Time as "h:mm AM/PM". Reads the stored wall-clock (UTC components) rather than
// localising, so the displayed time is exactly what was set — timezone left out.
export const formatTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    let h = d.getUTCHours();
    const m = d.getUTCMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
};

// Lifecycle events the brand/customer trigger. The backend logs these with a
// brand/customer actor (not 'system'), but in the thread they read as status
// updates, so they render as muted lines like the stage/mint lines — each on its
// own line (they are NOT condensed together).
export const LIFECYCLE_KINDS = new Set([
    'revision_shared', 'revision_approved', 'production_started', 'changes_requested', 'milestone',
]);

// Turn a bespoke thread into render items. Muted lines: the 'system' stage/mint
// lines (consecutive ones condense into one " · "-joined line) and the
// lifecycle-kind events above (each standalone). Everything else is a person
// message and passes through as a bubble. Returns render items:
// { system:true, key, body } or { system:false, key, m }.
export const groupThread = (messages) => {
    const groups = [];
    (messages || []).forEach(m => {
        if (m.actor === 'system') {                       // stage / mint — condensable
            const last = groups[groups.length - 1];
            if (last && last.system && last.condensable) last.bodies.push(m.body || '');
            else groups.push({ system: true, condensable: true, key: m.message_id, bodies: [m.body || ''], at: m.created_at });
        } else if (LIFECYCLE_KINDS.has(m.kind)) {         // muted, but standalone
            groups.push({ system: true, condensable: false, key: m.message_id, bodies: [m.body || ''], at: m.created_at });
        } else {
            groups.push({ system: false, key: m.message_id, m });
        }
    });
    return groups.map(g => g.system
        ? { system: true, key: g.key, at: g.at,
            body: g.bodies.length > 1
                ? g.bodies.map(b => b.replace(/\.\s*$/, '')).join(' · ')
                : g.bodies[0] }
        : g);
};

// The bespoke conversation thread, shared by the brand and customer dashboards so
// both render identically. `meActor` is the viewer's role ('brand' or 'customer');
// `otherName` labels the other party. Lifecycle/system entries render as muted
// lines; genuine dialogue renders as attributed bubbles.
export default function CommissionConversation({ messages, meActor, otherName }) {
    return (
        <div className="bsp-thread">
            {groupThread(messages).map(g => (
                g.system
                    ? <div className="bsp-sys" key={g.key}>
                        {g.body.replace(/\.\s*$/, '')}{g.at ? ` · ${formatDate(g.at)} · ${formatTime(g.at)}` : ''}
                    </div>
                    : <div className={`bsp-msg ${g.m.actor === meActor ? 'us' : 'them'}`} key={g.key}>
                        <div className="bsp-who">
                            {g.m.actor === meActor ? 'You' : otherName} · {g.m.created_at ? formatDate(g.m.created_at) : ''}{g.m.created_at ? ` · ${formatTime(g.m.created_at)}` : ''}
                        </div>
                        <div className="bsp-bub">{g.m.body}</div>
                    </div>
            ))}
        </div>
    );
}
